import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'Assets/icons/ChevronDown'
import { IconButton } from 'Components/Elements/IconButton/IconButton.styled'
import { DomainTableHeader, DomainTableHeaderText, DomainTableStyled } from './DomainsTable.styled'
import { ChevronUpIcon } from '@radix-ui/react-icons'
import { ASC, DESC } from 'Types/tables'
import { ReducedThreatGroupsBreakdown } from 'Features/home/routes/Home/types'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { NewDomainsTableData } from './NewDomainTableData'
import useOverallDomainPerformanceStore, {
  ACTIVE_DOMAIN_SORT_KEYS,
} from 'Features/home/store/overallDomainPerformance'
import { DomainEmptyHeader } from './ActiveDomainsTable.style'

export type TableRow = {
  address: string
  id: number
  invalidRateBotzapping?: number
  invalidRatePaidMarketing?: number
  active: boolean
  paidMarketingConnected?: boolean
  botZappingConnected?: boolean
  tagHashCode: string
  tagInstalled?: boolean
  visits?: number
  paidMarketingData?: ReducedThreatGroupsBreakdown & { visits: Array<[number, number]> }
  botzappingData?: ReducedThreatGroupsBreakdown & { visits: Array<[number, number]> }
  hasTrendsBotzapping?: boolean
  hasTrendsPaidMarketing?: boolean
  bzState?: number | null
  wordpress?: boolean
  tagUrl?: string
}

type NewActiveDomainsTableProps = {
  debouncedSearch: string
}

export const NewDomainsTable = ({ debouncedSearch }: NewActiveDomainsTableProps) => {
  const { t } = useTranslation('dashboard')
  const { data: planDetails } = useGetPlansDetails()
  const { sortDirection, sortDomainsBy, setSortDomainsBy, setSortDirection } =
    useOverallDomainPerformanceStore()

  const hasPaidMarketing = planDetails?.plans?.some(
    (product) => product.product.toLowerCase() === PRODUCT_TYPES.PAID_MARKETING
  )
  const hasBotZapping = planDetails?.plans?.some(
    (product) => product.product.toLowerCase() === PRODUCT_TYPES.BOT_ZAPPING
  )

  const getChevron = (columnName: string) => {
    const chevronColor = 'var(--colors-neutral)'
    if (sortDomainsBy === columnName) {
      return sortDirection === ASC ? (
        <ChevronUpIcon color={chevronColor} />
      ) : (
        <ChevronDown color={chevronColor} />
      )
    }
    return <ChevronDown color={chevronColor} />
  }

  const setSortWithDirection = (columnName: string) => {
    if (sortDomainsBy !== columnName) {
      setSortDomainsBy(columnName)
      setSortDirection(ASC)
    }
    if (sortDomainsBy === columnName) {
      setSortDirection(sortDirection === ASC ? DESC : ASC)
    }
    setSortDomainsBy(columnName)
  }

  return (
    <DomainTableStyled>
      <DomainTableHeader>
        <DomainTableHeaderText
          css={{ flex: '1 1 50%' }}
          onClick={() => setSortWithDirection(ACTIVE_DOMAIN_SORT_KEYS.ADDRESS)}
        >
          <IconButton aria-label="sort by domain">
            {t('overall_domain_performance.domain')}
            {getChevron(ACTIVE_DOMAIN_SORT_KEYS.ADDRESS)}
          </IconButton>
        </DomainTableHeaderText>
        {hasPaidMarketing ? (
          <DomainTableHeaderText
            css={{ flex: '1 1 25%', display: 'flex', justifyContent: 'center' }}
            onClick={() => setSortWithDirection(ACTIVE_DOMAIN_SORT_KEYS.PAID_MARKETING_TREND)}
          >
            <IconButton aria-label="sort by invalid rate">
              {t('overall_domain_performance.paid_marketing')}
              {getChevron(ACTIVE_DOMAIN_SORT_KEYS.PAID_MARKETING_TREND)}
            </IconButton>
          </DomainTableHeaderText>
        ) : (
          <DomainEmptyHeader></DomainEmptyHeader>
        )}
        {hasBotZapping ? (
          <DomainTableHeaderText
            css={{ flex: '1 1 25%', display: 'flex', justifyContent: 'center' }}
            onClick={() => setSortWithDirection(ACTIVE_DOMAIN_SORT_KEYS.BOT_MITIGATION_TREND)}
          >
            <IconButton aria-label="sort by invalid rate">
              {t('overall_domain_performance.bot_zapping')}
              {getChevron(ACTIVE_DOMAIN_SORT_KEYS.BOT_MITIGATION_TREND)}
            </IconButton>
          </DomainTableHeaderText>
        ) : (
          <DomainEmptyHeader></DomainEmptyHeader>
        )}
      </DomainTableHeader>
      <NewDomainsTableData debouncedSearch={debouncedSearch} />
    </DomainTableStyled>
  )
}
